import { useEffect, useRef } from "react";
import { SOUND_ASSETS } from "@/exported/pages/Configuration/NotificationSettings/components/soundAssets";
import { useShellHost } from "@/shell-handle/ShellHostProvider";

export const useNotificationSound = () => {
  const { store } = useShellHost();
  const audioRef = useRef<{ [key: string]: HTMLAudioElement }>({});

  useEffect(() => {
    const entries = Object.entries(SOUND_ASSETS);
    const audioMap: { [key: string]: HTMLAudioElement } = {};

    entries.forEach(([key, src]) => {
      audioMap[key] = new Audio(src);
    });

    audioRef.current = audioMap;

    Object.values(audioRef.current).forEach((audio) => {
      audio.volume = 1.0;
      audio.muted = false;

      audio.addEventListener("error", (e) => {
        console.error("[Sound] Failed to load audio:", e, audio.src);
      });

      audio.load();
    });

    return () => {
      Object.values(audioRef.current).forEach((audio) => {
        audio.pause();
        audio.src = "";
      });
    };
  }, []);

  useEffect(() => {
    let lastSounded = 0;
    let boundarySeen = false;
    let active = true;

    const subscription = store
      .observe(() => store.notifications.getSoundNotifications(lastSounded))
      .subscribe({
        next: async (rows) => {
          const pending = rows.filter((row) => (row.rowId ?? 0) > lastSounded);
          lastSounded = pending.at(-1)?.rowId ?? lastSounded;
          if (!boundarySeen) {
            boundarySeen = true;
            return;
          }

          try {
            for (const row of pending) {
              const soundScreenId =
                await store.notifications.getSoundScreenId();
              if (!active || store.screenId !== soundScreenId) return;

              const sound = await store.notifications.soundFor(row);
              const audio =
                sound === "MUTE" ? undefined : audioRef.current[sound];
              if (!active || !audio) continue;

              audio.volume = 1.0;
              audio.muted = false;
              audio.currentTime = 0;
              audio.play().catch((error) => {
                console.error(
                  "[Sound] Failed to play alarm:",
                  sound,
                  audio.src,
                  error,
                );
              });
            }
          } catch (error) {
            console.error("[Sound] notification subscription failed", error);
          }
        },
        error: (error) =>
          console.error("[Sound] notification subscription failed", error),
      });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [store]);
};
